import React, { useState } from 'react';
import { 
  Box, Flex, Heading, Text, Button, Image, SimpleGrid, VStack, HStack, 
  Badge, Input, FormControl, FormLabel, useToast
} from '@chakra-ui/react';
import { analyzeImageContent } from '../utils/contentAnalysis';
import SmartDesignSelector from './SmartDesignSelector';

// Sample images for testing
const SAMPLE_IMAGES = [
  {
    url: 'https://images.unsplash.com/photo-1544717297-fa95b6ee9643?w=400',
    name: 'Baseball Player Portrait',
    type: 'Portrait player photo'
  },
  {
    url: 'https://images.unsplash.com/photo-1566577739112-5180d4bf9390?w=400',
    name: 'Team Action Shot',
    type: 'Wide team photo'
  },
  {
    url: 'https://images.unsplash.com/photo-1431324155629-1a6deb1dec8d?w=400',
    name: 'Baseball Field',
    type: 'Landscape sports venue'
  },
  {
    url: 'https://images.unsplash.com/photo-1579952363873-27d3bfad9c0d?w=400',
    name: 'Baseball Equipment',
    type: 'Sports equipment close-up'
  }
];

const SmartDesignTester = ({ user }) => {
  const [selectedImage, setSelectedImage] = useState(SAMPLE_IMAGES[0].url);
  const [customImageUrl, setCustomImageUrl] = useState('');
  const [analysisResult, setAnalysisResult] = useState(null);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const toast = useToast();

  const handleImageSelect = (imageUrl) => {
    setSelectedImage(imageUrl);
    setCustomImageUrl('');
  };

  const handleCustomImageSubmit = () => {
    if (customImageUrl.trim()) {
      setSelectedImage(customImageUrl.trim());
    }
  };

  const testAnalysis = async () => {
    if (!selectedImage) return;
    
    setIsAnalyzing(true);
    try {
      const result = await analyzeImageContent(selectedImage);
      setAnalysisResult(result);
      
      toast({
        title: 'Analysis Complete!',
        description: `Detected: ${result.analysis.contentType} design with ${result.analysis.characteristics?.peopleCount || 1} person(s)`,
        status: 'success',
        duration: 3000,
      });
    } catch (error) {
      console.error('Analysis failed:', error);
      toast({
        title: 'Analysis Failed',
        description: 'Could not analyze the image. Please try another one.',
        status: 'error',
        duration: 3000,
      });
    } finally {
      setIsAnalyzing(false);
    }
  };

  return (
    <Box maxW="7xl" mx="auto" px={4} py={8}>
      {/* Header */}
      <Box mb={8}>
        <Heading size="xl" mb={2} color="gray.800">
          Smart Design Generator - Test Mode
        </Heading>
        <Text color="gray.600" fontSize="lg">
          Test the AI content analysis with sample images or your own URLs
        </Text>
      </Box>

      {/* Image Selection */}
      <Box mb={8} p={6} bg="white" borderRadius="xl" boxShadow="sm" border="1px" borderColor="gray.200">
        <Heading size="md" mb={4}>1. Choose Test Image</Heading>
        
        {/* Sample Images */}
        <SimpleGrid columns={{ base: 2, md: 4 }} spacing={4} mb={6}>
          {SAMPLE_IMAGES.map((sample, index) => (
            <Box
              key={index}
              cursor="pointer"
              onClick={() => handleImageSelect(sample.url)}
              border="2px solid"
              borderColor={selectedImage === sample.url ? "blue.400" : "gray.200"}
              borderRadius="lg"
              overflow="hidden"
              _hover={{ borderColor: "blue.300" }}
              transition="border-color 0.2s"
            >
              <Image 
                src={sample.url} 
                alt={sample.name}
                w="100%"
                h="120px"
                objectFit="cover"
              />
              <Box p={2}>
                <Text fontSize="sm" fontWeight="medium">{sample.name}</Text>
                <Text fontSize="xs" color="gray.500">{sample.type}</Text>
              </Box>
            </Box>
          ))}
        </SimpleGrid>

        {/* Custom Image URL */}
        <Box>
          <Text fontSize="sm" fontWeight="medium" mb={2}>Or use your own image URL:</Text>
          <HStack>
            <Input
              placeholder="https://example.com/image.jpg"
              value={customImageUrl}
              onChange={(e) => setCustomImageUrl(e.target.value)}
            />
            <Button onClick={handleCustomImageSubmit} colorScheme="blue">
              Use This Image
            </Button>
          </HStack>
        </Box>
      </Box>

      {/* Selected Image Preview */}
      {selectedImage && (
        <Box mb={8} p={6} bg="white" borderRadius="xl" boxShadow="sm" border="1px" borderColor="gray.200">
          <Flex align="center" justify="space-between" mb={4}>
            <Heading size="md">2. Preview & Analyze</Heading>
            <Button 
              onClick={testAnalysis} 
              colorScheme="green" 
              isLoading={isAnalyzing}
              loadingText="Analyzing..."
            >
              Run AI Analysis
            </Button>
          </Flex>
          
          <HStack spacing={6} align="start">
            <Box>
              <Image 
                src={selectedImage} 
                alt="Selected test image"
                maxW="300px"
                maxH="300px"
                objectFit="contain"
                borderRadius="lg"
                border="1px solid"
                borderColor="gray.200"
              />
            </Box>
            
            {analysisResult && (
              <Box flex="1" p={4} bg="gray.50" borderRadius="lg">
                <Text fontWeight="bold" mb={2}>Analysis Results:</Text>
                <VStack align="start" spacing={1} fontSize="sm">
                  <Text><strong>Content Type:</strong> {analysisResult.analysis.contentType}</Text>
                  <Text><strong>Suggested Layout:</strong> {analysisResult.analysis.suggestedLayout}</Text>
                  <Text><strong>Orientation:</strong> {
                    analysisResult.analysis.characteristics?.isPortrait ? 'Portrait' : 
                    analysisResult.analysis.characteristics?.isLandscape ? 'Landscape' : 'Square'
                  }</Text>
                  <Text><strong>People Count:</strong> {analysisResult.analysis.characteristics?.peopleCount}</Text>
                  <Text><strong>Brightness:</strong> {analysisResult.analysis.characteristics?.brightness}</Text>
                  <Text><strong>Confidence:</strong> {Math.round((analysisResult.analysis.confidence?.contentType || 0) * 100)}%</Text>
                </VStack>
                
                {analysisResult.analysis.recommendations && (
                  <Box mt={3}>
                    <Text fontWeight="bold" mb={1}>Recommendations:</Text>
                    {analysisResult.analysis.recommendations.slice(0, 2).map((rec, index) => (
                      <Badge key={index} colorScheme="green" mr={2} mb={1}>
                        {rec.type}: {Math.round(rec.confidence * 100)}%
                      </Badge>
                    ))}
                  </Box>
                )}
              </Box>
            )}
          </HStack>
        </Box>
      )}

      {/* Smart Design Selector */}
      <Box p={6} bg="white" borderRadius="xl" boxShadow="sm" border="1px" borderColor="gray.200">
        <Heading size="md" mb={4}>3. Smart Template Suggestions</Heading>
        <SmartDesignSelector uploadedImageURL={selectedImage} user={user} />
      </Box>
    </Box>
  );
};

export default SmartDesignTester; 